import {
  Navigate,
  useLocation,
  redirect,
  LoaderFunction,
  LoaderFunctionArgs,
} from 'react-router-dom'
import { isLogin } from 'utils/token'
import { DEFAULT_PATH, LOGIN_PAGE_PATH } from 'utils/consts/path'
import { getUserDataForRoutes } from 'utils/hooks/useSelectedCompany'
import { Roles } from 'utils/consts/userRoles'
import store from 'store'

const RedirectToLogin = () => {
  const location = useLocation()
  return <Navigate to={LOGIN_PAGE_PATH} state={{ from: location }} />
}

export const RequireAuth = ({ children }) => {
  const userLoggedIn = isLogin()
  return userLoggedIn ? children : <RedirectToLogin />
}

interface GuardArgs {
  roles?: string[]
  categories?: string[]
  isVerified?: boolean
  requireAccessToSellWine?: boolean
  requireAccessToRetail?: boolean
  rejectWhenProhibited?: boolean
}

const isPassingGuard =
  (args: GuardArgs) =>
  ({
    role,
    category,
    isVerified,
    hasAccessToSellWine,
    hasAccessToRetail,
    isProhibited,
  }: {
    role: string
    category: string
    isVerified: boolean
    hasAccessToSellWine: boolean
    hasAccessToRetail: boolean
    isProhibited: boolean
  }) => {
    const {
      roles,
      categories,
      isVerified: requiredVerification,
      requireAccessToSellWine,
      requireAccessToRetail,
      rejectWhenProhibited,
    } = args

    if (rejectWhenProhibited && isProhibited) return false

    const roleMatch = !roles || roles.includes(role)
    const companyCategoryMatch =
      role === Roles.PERSON
        ? !categories || categories.includes(category)
        : true
    const verificationPassed =
      role === Roles.PERSON && requiredVerification ? isVerified : true
    const sellWineAccessPassed = requireAccessToSellWine
      ? hasAccessToSellWine
      : true
    const retailAccessPassed =
      role !== Roles.PERSON ||
      (requireAccessToRetail ? hasAccessToRetail : true)

    return (
      roleMatch &&
      companyCategoryMatch &&
      verificationPassed &&
      sellWineAccessPassed &&
      retailAccessPassed
    )
  }

export const protectedLoader = (
  args: GuardArgs,
  nextLoader?: LoaderFunction
) => {
  const guardFn = isPassingGuard(args)

  return async (loaderArgs: LoaderFunctionArgs, handlerCtx: unknown) => {
    const state = store.getState()
    const data = getUserDataForRoutes(state)
    const pass = guardFn(data)
    if (!pass) {
      if (!isLogin()) {
        const url = new URL(loaderArgs.request.url)
        return redirect(
          `${LOGIN_PAGE_PATH}?from=${encodeURIComponent(url.pathname)}`
        )
      }
      // Check if user is coming from onboarding - allow access
      const fromOnboarding = sessionStorage.getItem('fromOnboarding')
      if (fromOnboarding === 'true') {
        sessionStorage.removeItem('fromOnboarding')
        return nextLoader ? nextLoader(loaderArgs, handlerCtx) : null
      }
      return redirect(DEFAULT_PATH)
    }
    return nextLoader ? nextLoader(loaderArgs, handlerCtx) : null
  }
}
